/**
 * The rail's action bar (fix D / R5): ONE 48 px row — the game actions on the
 * LEFT as compact text+icon buttons, the two utilities on the RIGHT as icon
 * buttons. It replaces M1a's wrapping grid of 60 px tiles (R6.2).
 *
 * Three faces, and the one that is not on screen is NOT mounted: a spectator has
 * no draw to offer and a finished game has nothing to resign, so those leaves
 * must not exist rather than merely disable themselves (UX spec §1 rule 3).
 *
 * OWNER 2026-10-06: the sound toggle is OUT of this bar — the theme switch was
 * the only utility left on the right. The preference itself is untouched
 * (`rail/useSounds.ts` still reads it, and the PHONE's `Toolbar` still carries
 * `SoundTile`), so sound keeps working and keeps whatever the player last set.
 * Fix Z2.4 adds the parental-controls CTA before it — mounted only for an
 * account that holds any custom rules, same `ParentalControlsButton` shape.
 */
import React from 'react';
import {StyleSheet, View} from 'react-native';

import {SPACE} from '../../../theme/tokens';
import {useV2Theme} from '../../../theme/useV2Theme';
import {
  AbortButton,
  ArenaPauseButton,
  BerserkButton,
  DrawButton,
  PlayAgainButton,
  RematchButton,
  ResignButton,
  StopWatchingButton,
  ThemeButton,
} from './buttons';
import {ActionBarModel} from './model';
import ParentalControlsButton from './ParentalControlsButton';

const BAR_HEIGHT = 48;

const leftFor = (face: ActionBarModel['face']): React.ReactNode => {
  if (face === 'watch') {
    return <StopWatchingButton />;
  }
  if (face === 'over') {
    return (
      <>
        <RematchButton />
        <PlayAgainButton />
      </>
    );
  }
  return (
    <>
      <DrawButton />
      <ResignButton />
      {/* T2: an arena game only — hidden for a Swiss/casual game */}
      <ArenaPauseButton />
      <AbortButton />
      <BerserkButton />
    </>
  );
};

export default React.memo(function ActionBarView({face}: ActionBarModel) {
  const tokens = useV2Theme(s => s.tokens);
  return (
    <View
      testID="v2-action-bar"
      style={[styles.bar, {boxShadow: `inset 0 1px 0 ${tokens.line}`} as any]}>
      <View style={styles.left}>{leftFor(face)}</View>
      <View style={styles.right}>
        <ParentalControlsButton />
        <ThemeButton />
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  bar: {
    height: BAR_HEIGHT,
    flexGrow: 0,
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACE.sm,
  },
  left: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    minWidth: 0,
    gap: SPACE.xs,
  },
  right: {
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 0,
    flexShrink: 0,
    gap: SPACE.xs,
  },
});
